import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell, TrendingUp, TrendingDown, ShieldAlert, Sparkles } from "lucide-react";
import api from "../../lib/api";

const ICONS = {
  sentiment_flip: (m) => (m?.to === "RISK_OFF" ? TrendingDown : TrendingUp),
  plan_invalidated: () => ShieldAlert,
};
const COLORS = {
  sentiment_flip: (m) => (m?.to === "RISK_OFF" ? "var(--red)" : "var(--green)"),
  plan_invalidated: () => "var(--amber)",
};

export default function AlertsBell() {
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await api.getOutlookAlerts();
      setAlerts(d.alerts || []);
      setUnread(d.unread || 0);
    } catch (_) { /* ignore (e.g., not authed yet) */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    const onUpd = () => load();
    window.addEventListener("outlook-alerts-updated", onUpd);
    return () => { clearInterval(t); window.removeEventListener("outlook-alerts-updated", onUpd); };
  }, [load]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.markOutlookAlertsRead(); setUnread(0); } catch (_) { /* ignore */ }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="alerts-bell-button"
        onClick={toggle}
        className="relative grid place-items-center rounded-lg ym-hover"
        style={{ width: 36, height: 36, background: "var(--panel-2)", border: "1px solid var(--border)" }}
      >
        <Bell size={16} style={{ color: unread ? "var(--accent-soft)" : "var(--text-muted)" }} />
        {unread > 0 && (
          <span
            data-testid="alerts-unread-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full text-[9px] font-bold text-white"
            style={{ background: "var(--red)" }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="alerts-dropdown"
          className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1.5rem)] rounded-xl overflow-hidden z-50"
          style={{ background: "var(--panel)", border: "1px solid var(--border)", boxShadow: "0 20px 50px -12px rgba(0,0,0,.6)" }}
        >
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
            <span className="text-[13px] font-bold">Outlook Alerts</span>
            <span className="text-[11px]" style={{ color: "var(--text-dim)" }}>{alerts.length} recent</span>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {alerts.length === 0 && (
              <div className="flex flex-col items-center py-8 text-center" data-testid="alerts-empty">
                <Sparkles size={22} style={{ color: "var(--accent-soft)" }} />
                <p className="mt-2 text-[12px]" style={{ color: "var(--text-dim)" }}>
                  No alerts yet. Generate an AI Market Outlook to start monitoring sentiment flips and plan invalidations.
                </p>
              </div>
            )}
            {alerts.map((a) => {
              const Icon = (ICONS[a.type] || (() => Bell))(a.meta);
              const color = (COLORS[a.type] || (() => "var(--accent-soft)"))(a.meta);
              return (
                <div key={a.id} data-testid="alert-item"
                  className="flex gap-3 px-4 py-3" style={{ borderBottom: "1px solid var(--border)", background: a.read ? "transparent" : "var(--panel-2)" }}>
                  <div className="grid place-items-center rounded-lg shrink-0" style={{ width: 30, height: 30, background: "var(--panel)", border: "1px solid var(--border)" }}>
                    <Icon size={15} style={{ color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold" style={{ color: "var(--text)" }}>{a.title}</div>
                    <div className="text-[11.5px] leading-snug" style={{ color: "var(--text-muted)" }}>{a.message}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: "var(--text-dim)" }}>{new Date(a.at).toLocaleString()}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
